/**
 * Pakun tööd — Job creation wizard (Töö → Asukoht → Tingimused → Kontrolli ja avalda)
 *
 * Fixes the P0 "map flash" bug: this route does NOT redirect through the
 * homepage. Non-company users are handled inline (private user → company
 * mini-form; logged-out → /login?next=/jobs/create).
 *
 * All UI text goes through i18n (auth.* / jobWizard.*). Autosave to
 * /api/jobs/drafts with debounce. Publish goes through /api/jobs with an
 * idempotent draft_id so one click === one job.
 */
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth, useLanguage, api } from '@/lib/context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { GlobalNav } from '@/components/GlobalNav';
import {
  Briefcase, MapPin, ClipboardCheck, CheckCircle2, Save, Loader2,
  Users, User as UserIcon, UserPlus, Building2, ArrowLeft, ArrowRight,
  Eye, Edit3, X, AlertCircle, ShieldCheck,
} from 'lucide-react';

// ---------- Static reference data ----------

const CATEGORIES = [
  { id: 'metal_installation', label: 'metallkonstruktsioonide paigaldus' },
  { id: 'welding', label: 'keevitus' },
  { id: 'industrial_installation', label: 'tööstuslik paigaldus' },
  { id: 'construction', label: 'ehitus' },
  { id: 'electrical', label: 'elektritööd' },
  { id: 'plumbing', label: 'torutööd' },
  { id: 'doors_windows', label: 'uste ja akende paigaldus' },
  { id: 'logistics', label: 'logistika' },
  { id: 'transport', label: 'transport' },
  { id: 'other', label: 'muu' },
];

const COUNTRIES = [
  { code: 'EE', labelKey: 'country.EE' },
  { code: 'FI', labelKey: 'country.FI' },
  { code: 'NO', labelKey: 'country.NO' },
  { code: 'SE', labelKey: 'country.SE' },
  { code: 'DK', labelKey: 'country.DK' },
  { code: 'DE', labelKey: 'country.DE' },
  { code: 'PL', labelKey: 'country.PL' },
  { code: 'LV', labelKey: 'country.LV' },
  { code: 'LT', labelKey: 'country.LT' },
  { code: 'NL', labelKey: 'country.NL' },
  { code: 'GB', labelKey: 'country.GB' },
];

// Fallback labels — used when the i18n key resolves to itself (missing key)
const COUNTRY_LABELS_FALLBACK = {
  EE: 'Estonia', FI: 'Finland', NO: 'Norway', SE: 'Sweden', DK: 'Denmark',
  DE: 'Germany', PL: 'Poland', LV: 'Latvia', LT: 'Lithuania',
  NL: 'Netherlands', GB: 'United Kingdom',
};

const countryLabel = (code, t) => {
  if (!code) return '';
  const key = `country.${code}`;
  const translated = t(key);
  if (translated && translated !== key) return translated;
  return COUNTRY_LABELS_FALLBACK[code] || code;
};

const LANGUAGES = [
  { code: 'et', label: 'eesti' },
  { code: 'en', label: 'inglise' },
  { code: 'fi', label: 'soome' },
  { code: 'sv', label: 'rootsi' },
  { code: 'ru', label: 'vene' },
  { code: 'lv', label: 'läti' },
  { code: 'lt', label: 'leedu' },
  { code: 'pl', label: 'poola' },
];

const LANG_LEVELS = ['basic', 'working', 'fluent'];

// NOTE: The hardcoded metalwork SKILLS array was removed. Skill / certificate
// suggestions are now fetched dynamically from `POST /api/jobs/skill-suggestions`
// based on the actual job title, description and category. See the
// `useSkillSuggestions` hook below.


const DEFAULT_SCREENING = [
  'Mitu aastat on sul vastavat töökogemust?',
  'Millal saad tööd alustada?',
  'Millised vajalikud tunnistused sul olemas on?',
  'Milliseid keeli ja mis tasemel räägid?',
  'Kas sul on õigus valitud riigis töötada?',
];

const EMPTY_FORM = {
  // Step 1: Töö
  title: '',
  category: '',
  extra_skills: [],
  who_type: '',
  worker_count: 1, // legacy back-compat; source of truth is workforce_* below
  // Workforce (new — flexible count)
  workforce_amount_type: 'exact', // exact | range | ongoing | undecided
  workforce_exact_amount: 1,
  workforce_minimum: '',
  workforce_maximum: '',
  show_workforce_amount: true,
  team_options: { own_foreman: false, own_vehicle: false, own_tools: false },
  description: '',
  experience: '',
  skills: [], // [{name, required}]
  // Languages — each entry is {code, level, requirement: 'required'|'preferred'}
  languages: [],
  required_language_logic: 'any',   // any | all
  team_language_requirement: 'at_least_one_member', // at_least_one_member | every_member
  // Step 2: Asukoht
  country: '',
  city: '',
  address_line: '',
  address_visibility: 'candidates_only',
  work_arrangement: '',
  travel_between_sites: '',
  // Step 3: Tingimused
  start_type: '',
  start_date: '',
  duration: '',
  cooperation_type: '',
  hours_per_day: '',
  days_per_week: '',
  hours_per_week: '',
  overtime_available: false,
  weekend_work: false,
  night_work: false,
  rotation_type: '',
  rotation_pattern: '',
  salary_type: 'agreed', // hourly | monthly | project | agreed
  salary_min: '',
  salary_max: '',
  salary_currency: 'EUR',
  salary_gross_net: 'gross',
  accommodation_type: '',
  accommodation_cost: '',
  transport_to_country: '',
  transport_local: '',
  equipment_provided: [],
  equipment_worker_own: [],
  benefits: [],
  // Step 4: Kontrolli ja avalda
  application_method: 'keikka_only',
  contact_person: '',
  contact_email: '',
  contact_phone: '',
  screening_questions: [], // [{question, required}]
};

const totalSteps = 4;

// ---------- Storage keys ----------
const LS_INTENT = 'keikka.jobCreate.intent';
const LS_DRAFT = 'keikka.jobCreate.draft';

// Save intent + draft to localStorage before an auth redirect so we can
// restore after login/register/reset-password.
export function stashJobCreateIntent(draft) {
  try {
    localStorage.setItem(LS_INTENT, '1');
    if (draft) localStorage.setItem(LS_DRAFT, JSON.stringify(draft));
  } catch (_) {}
}
export function hasStashedJobCreateIntent() {
  try { return localStorage.getItem(LS_INTENT) === '1'; } catch (_) { return false; }
}
export function clearStashedJobCreateIntent() {
  try {
    localStorage.removeItem(LS_INTENT);
    localStorage.removeItem(LS_DRAFT);
  } catch (_) {}
}

// ---------- Small UI helpers ----------

const StepDot = ({ n, label, active, done, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={`wiz-step-${n}`}
    className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm transition
      ${active ? 'bg-brand text-white shadow-sm'
        : done ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
        : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
  >
    <span className={`h-6 w-6 rounded-full grid place-items-center text-xs font-semibold
      ${active ? 'bg-white/25' : done ? 'bg-emerald-600 text-white' : 'bg-white text-slate-500'}`}>
      {done ? <CheckCircle2 className="h-4 w-4" /> : n}
    </span>
    <span className="hidden sm:inline font-medium">{label}</span>
  </button>
);

const CardSelect = ({ options, value, onChange, testid }) => (
  <div className="grid sm:grid-cols-2 gap-3" data-testid={testid}>
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        onClick={() => onChange(o.value)}
        data-testid={`${testid}-opt-${o.value}`}
        className={`text-left p-4 rounded-xl border-2 transition
          ${value === o.value
            ? 'border-brand bg-orange-50 shadow-sm'
            : 'border-slate-200 hover:border-slate-300 bg-white'}`}
      >
        <div className="flex items-center gap-2 font-semibold text-slate-900">
          {o.icon}
          {o.label}
        </div>
        {o.desc && <div className="text-xs text-slate-500 mt-1">{o.desc}</div>}
      </button>
    ))}
  </div>
);

// ---------- Company mini-form (for private users who click Pakun tööd) ----------

const CompanyBootstrap = ({ onDone }) => {
  const { t } = useLanguage();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    company_name: '', country: 'EE', reg_number: '',
    contact_person: '', email: '', phone: '',
  });
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.country || !form.reg_number || !form.contact_person || !form.email) {
      toast.error(t('jobWizard.errors.companyRequired'));
      return;
    }
    setSaving(true);
    try {
      await api.post('/companies/upgrade-to-company', {
        company_name: form.company_name,
        country: form.country,
        registration_number: form.reg_number,
        contact_person: form.contact_person,
        contact_email: form.email,
        contact_phone: form.phone,
      });
      toast.success(t('jobWizard.bootstrap.saved'));
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('common.error'));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Card className="max-w-lg mx-auto">
      <CardContent className="pt-6">
        <div className="flex items-center gap-2 mb-1 text-brand">
          <Building2 className="h-5 w-5" />
          <span className="text-xs font-semibold uppercase tracking-wide">
            {t('jobWizard.bootstrap.tag')}
          </span>
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-2">
          {t('jobWizard.bootstrap.title')}
        </h2>
        <p className="text-sm text-slate-600 mb-5">
          {t('jobWizard.bootstrap.subtitle')}
        </p>
        <form className="space-y-4" onSubmit={submit}>
          <div>
            <Label>{t('jobWizard.bootstrap.companyName')} *</Label>
            <Input value={form.company_name} onChange={(e) => upd('company_name', e.target.value)}
                   data-testid="bootstrap-company-name" required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t('jobWizard.bootstrap.country')} *</Label>
              <Select value={form.country} onValueChange={(v) => upd('country', v)}>
                <SelectTrigger data-testid="bootstrap-country"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map((c) => <SelectItem key={c.code} value={c.code}>{countryLabel(c.code, t)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('jobWizard.bootstrap.regNumber')} *</Label>
              <Input value={form.reg_number} onChange={(e) => upd('reg_number', e.target.value)}
                     data-testid="bootstrap-reg-number" required />
            </div>
          </div>
          <div>
            <Label>{t('jobWizard.bootstrap.contactPerson')} *</Label>
            <Input value={form.contact_person} onChange={(e) => upd('contact_person', e.target.value)}
                   data-testid="bootstrap-contact-person" required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t('jobWizard.bootstrap.email')} *</Label>
              <Input type="email" value={form.email} onChange={(e) => upd('email', e.target.value)}
                     data-testid="bootstrap-email" required />
            </div>
            <div>
              <Label>{t('jobWizard.bootstrap.phone')}</Label>
              <Input value={form.phone} onChange={(e) => upd('phone', e.target.value)}
                     data-testid="bootstrap-phone" />
            </div>
          </div>
          <Button type="submit" disabled={saving} className="w-full" data-testid="bootstrap-submit">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('jobWizard.bootstrap.submit')}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// ---------- Live preview ----------

// Public-facing workforce line — respects show_workforce_amount visibility.
// If `publicView` is true and the show flag is off, hides the exact number.
export const workforceDisplay = (f, t, { publicView = false } = {}) => {
  if (!f) return '';
  const who = f.who_type;
  if (who === 'single_worker') return t('jobWizard.publicWorkforce.single');
  const type = f.workforce_amount_type || 'exact';
  const hidden = publicView && !f.show_workforce_amount && (type === 'exact' || type === 'range');
  if (hidden) return t('jobWizard.publicWorkforce.hidden');
  const isTeam = who === 'team';
  const kind = isTeam ? 'team' : 'worker';
  if (type === 'ongoing') return t(`jobWizard.publicWorkforce.ongoing_${kind}`);
  if (type === 'undecided') return t(`jobWizard.publicWorkforce.undecided_${kind}`);
  if (type === 'exact') {
    const n = Number(f.workforce_exact_amount) || Number(f.worker_count) || 0;
    if (!n) return '';
    return t(`jobWizard.publicWorkforce.exact_${kind}`, { count: n })
      .replace('{count}', String(n));
  }
  if (type === 'range') {
    const mn = Number(f.workforce_minimum) || 0;
    const mx = Number(f.workforce_maximum) || 0;
    if (!mn && !mx) return '';
    if (!mx || mn === mx) return t(`jobWizard.publicWorkforce.exact_${kind}`, { count: mn }).replace('{count}', String(mn));
    return t(`jobWizard.publicWorkforce.range_${kind}`).replace('{min}', String(mn)).replace('{max}', String(mx));
  }
  return '';
};

const LivePreview = ({ f, t }) => {
  const hasAnything = f.title || f.description || f.city || f.category;
  const salaryLine = () => {
    if (f.salary_type === 'agreed' || !f.salary_min) return t('jobWizard.preview.salaryAgreed');
    const range = f.salary_max && f.salary_max !== f.salary_min
      ? `${f.salary_min}–${f.salary_max}` : `${f.salary_min}`;
    const period = f.salary_type === 'hourly' ? '/h'
      : f.salary_type === 'monthly' ? '/kuu'
      : f.salary_type === 'project' ? ` (${t('jobWizard.preview.perProject')})` : '';
    return `${range} ${f.salary_currency}${period}`;
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        <Eye className="h-4 w-4" />
        {t('jobWizard.preview.title')}
      </div>
      <div className="p-5 space-y-3">
        {!hasAnything && (
          <p className="text-sm text-slate-400">
            {t('jobWizard.preview.empty')}
          </p>
        )}
        {f.title && (
          <div>
            <h3 className="text-xl font-bold text-slate-900" data-testid="preview-title">{f.title}</h3>
            <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-slate-500">
              {f.category && (
                <span className="inline-flex items-center gap-1">
                  <Briefcase className="h-3.5 w-3.5" />
                  {CATEGORIES.find((c) => c.id === f.category)?.label || f.category}
                </span>
              )}
              {(f.city || f.country) && (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
              {[f.city, countryLabel(f.country, t)].filter(Boolean).join(', ')}
                </span>
              )}
            </div>
          </div>
        )}
        {f.description && (
          <p className="text-sm text-slate-700 whitespace-pre-wrap line-clamp-6">
            {f.description}
          </p>
        )}
        <div className="grid grid-cols-2 gap-2 text-xs">
          {(f.salary_type || f.salary_min) && (
            <div className="p-2 rounded bg-slate-50">
              <div className="text-slate-400">{t('jobWizard.preview.salary')}</div>
              <div className="font-semibold text-slate-900">{salaryLine()}</div>
            </div>
          )}
          {f.duration && (
            <div className="p-2 rounded bg-slate-50">
              <div className="text-slate-400">{t('jobWizard.preview.duration')}</div>
              <div className="font-semibold text-slate-900">{f.duration}</div>
            </div>
          )}
          {f.rotation_type === 'yes' && f.rotation_pattern && (
            <div className="p-2 rounded bg-slate-50">
              <div className="text-slate-400">{t('jobWizard.preview.rotation')}</div>
              <div className="font-semibold text-slate-900">{f.rotation_pattern}</div>
            </div>
          )}
          {f.accommodation_type && (
            <div className="p-2 rounded bg-slate-50">
              <div className="text-slate-400">{t('jobWizard.preview.accommodation')}</div>
              <div className="font-semibold text-slate-900 text-[11px]">{t(`jobWizard.opts.accommodation.${f.accommodation_type}`, f.accommodation_type)}</div>
            </div>
          )}
        </div>
        {f.who_type && (
          <div className="text-xs" data-testid="preview-workforce">
            <span className="text-slate-400 mr-1">{t('jobWizard.preview.who')}:</span>
            <Badge variant="secondary">{workforceDisplay(f, t, { publicView: true }) || t(`jobWizard.opts.who.${f.who_type}`, f.who_type)}</Badge>
          </div>
        )}

        {/* Languages — split by requirement */}
        {f.languages && f.languages.length > 0 && (
          <div className="text-xs" data-testid="preview-languages">
            {(() => {
              const req = f.languages.filter((l) => (l.requirement || 'required') === 'required');
              const pref = f.languages.filter((l) => (l.requirement || 'required') === 'preferred');
              return (
                <div className="space-y-2">
                  {req.length > 0 && (
                    <div>
                      <div className="text-slate-400">{t('jobWizard.preview.langRequired')}</div>
                      <div className="text-slate-800">
                        {req.map((l, i) => (
                          <span key={l.code}>
                            {i > 0 && ', '}
                            {LANGUAGES.find((x) => x.code === l.code)?.label || l.code} — {t(`jobWizard.opts.lang.${l.level || 'working'}`)}
                          </span>
                        ))}
                      </div>
                      {req.length >= 2 && f.required_language_logic === 'any' && (
                        <div className="text-[11px] text-slate-500 mt-1">{t('jobWizard.preview.langAnyNote')}</div>
                      )}
                      {(f.who_type === 'team' || f.who_type === 'subcontractor') && f.team_language_requirement === 'at_least_one_member' && (
                        <div className="text-[11px] text-slate-500 mt-1">{t('jobWizard.preview.teamLangNote')}</div>
                      )}
                    </div>
                  )}
                  {pref.length > 0 && (
                    <div>
                      <div className="text-slate-400">{t('jobWizard.preview.langPreferred')}</div>
                      <div className="text-slate-800">
                        {pref.map((l, i) => (
                          <span key={l.code}>
                            {i > 0 && ', '}
                            {LANGUAGES.find((x) => x.code === l.code)?.label || l.code} — {t(`jobWizard.opts.lang.${l.level || 'working'}`)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

// ---------- Publish confirmation dialog ----------

const PublishConfirm = ({ open, onCancel, onConfirm, publishing }) => {
  const { t } = useLanguage();
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !publishing) onCancel(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('jobWizard.confirmPublish.title')}</DialogTitle>
          <DialogDescription>{t('jobWizard.confirmPublish.body')}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={publishing} data-testid="confirm-back">
            {t('jobWizard.confirmPublish.back')}
          </Button>
          <Button onClick={onConfirm} disabled={publishing} data-testid="confirm-publish">
            {publishing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {t('jobWizard.confirmPublish.yes')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Main wizard ----------

const AutosaveBadge = ({ status, t }) => {
  const map = {
    idle: null,
    saving: { c: 'text-slate-500', l: t('jobWizard.autosave.saving'), i: <Loader2 className="h-3.5 w-3.5 animate-spin" /> },
    saved: { c: 'text-emerald-600', l: t('jobWizard.autosave.saved'), i: <CheckCircle2 className="h-3.5 w-3.5" /> },
    failed: { c: 'text-red-600', l: t('jobWizard.autosave.failed'), i: <AlertCircle className="h-3.5 w-3.5" /> },
  };
  const s = map[status];
  if (!s) return null;
  return <div className={`inline-flex items-center gap-1 text-xs ${s.c}`} data-testid="autosave-status">
    {s.i}<span>{s.l}</span>
  </div>;
};

export default function CreateJobWizardPage() {
  const { user, loading } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY_FORM);
  const [draftId, setDraftId] = useState(null);
  const [autosave, setAutosave] = useState('idle');
  const [errors, setErrors] = useState({});
  const [showConfirm, setShowConfirm] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showPreviewSheet, setShowPreviewSheet] = useState(false);
  const [needsCompany, setNeedsCompany] = useState(false);

  const saveTimer = useRef(null);
  const initialLoadDone = useRef(false);
  // When true, we found an existing server draft and are asking the user
  // whether to resume it. Prevents stale state from leaking into a fresh
  // "Lisa töökuulutus" flow.
  const [pendingDraft, setPendingDraft] = useState(null); // {draft_id, step, data, updated_at} or null

  // ---------- Auth gate (fixes map-flash bug) ----------
  useEffect(() => {
    if (loading) return;
    if (!user) {
      // Stash intent + draft to localStorage and redirect to login with ?next=
      stashJobCreateIntent(form);
      navigate(`/login?next=${encodeURIComponent(location.pathname)}`, { replace: true });
      return;
    }
    if (user.role === 'worker' || user.role === 'private') {
      setNeedsCompany(true);
      return;
    }
    if (user.role === 'admin') {
      // Admin visitors: allow the wizard but they generally do not publish jobs.
      setNeedsCompany(false);
      return;
    }
    setNeedsCompany(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading]);

  // ---------- Load existing draft ----------
  //
  // A previous version silently loaded ANY unfinished server draft on every
  // wizard mount. That caused stale state (e.g. metalwork skills) to leak
  // into a fresh "Lisa töökuulutus" click. The corrected flow:
  //
  //   1. Explicit `?draftId=X` in the URL → load THAT draft.
  //   2. `?resume=1` in the URL → load the most recent unfinished draft.
  //   3. Post-login stash (localStorage LS_INTENT) → restore the exact
  //      form the user was filling in before they got sent to /login.
  //   4. Otherwise → START FRESH. If the server still has a recent draft
  //      we surface it as a "Continue previous draft?" dialog so the user
  //      chooses whether to resume — never silently.
  //
  // The draft is still autosaved throughout, so nothing is lost.
  useEffect(() => {
    if (loading || !user || needsCompany) return;
    if (initialLoadDone.current) return;
    initialLoadDone.current = true;
    (async () => {
      const explicitDraftId = searchParams.get('draftId');
      const resumeRequested = searchParams.get('resume') === '1';
      const duplicateFromId = searchParams.get('duplicate');
      const startFresh = searchParams.get('new') === '1';
      let loadedFromStash = false;

      // 1) Post-login stash — restore the exact form the user was filling.
      //    Only trusted when LS_INTENT flag is set (i.e. we actually
      //    redirected them through /login).
      try {
        if (hasStashedJobCreateIntent()) {
          const stashed = localStorage.getItem(LS_DRAFT);
          if (stashed) {
            const parsed = JSON.parse(stashed);
            if (parsed && typeof parsed === 'object') {
              setForm((f) => ({ ...f, ...parsed }));
              loadedFromStash = true;
            }
          }
        }
      } catch (_) {}
      // Always consume the intent flag so it doesn't leak into future mounts.
      clearStashedJobCreateIntent();

      // If user asked for a completely fresh form, honour it and stop.
      if (startFresh && !explicitDraftId) return;

      // 2) Explicit draftId in URL → load that draft (owner check on server).
      if (explicitDraftId) {
        try {
          const res = await api.get('/jobs/drafts/mine');
          if (res.data?.draft_id === explicitDraftId) {
            setDraftId(res.data.draft_id);
            if (res.data.data) setForm((f) => ({ ...f, ...res.data.data }));
            if (res.data.step) setStep(Math.min(totalSteps, Math.max(1, Number(res.data.step))));
          }
        } catch (_) {}
        return;
      }

      // 3) Duplicate flow → clone an existing published job into the form.
      if (duplicateFromId) {
        try {
          const res = await api.get(`/jobs/${duplicateFromId}`);
          if (res.data?.wizard_data) {
            setForm((f) => ({ ...f, ...res.data.wizard_data }));
          }
        } catch (_) {}
        return;
      }

      // 4) `?resume=1` → load the most recent draft (deliberate action).
      if (resumeRequested) {
        try {
          const res = await api.get('/jobs/drafts/mine');
          if (res.data?.draft_id) {
            setDraftId(res.data.draft_id);
            if (res.data.data) setForm((f) => ({ ...f, ...res.data.data }));
            if (res.data.step) setStep(Math.min(totalSteps, Math.max(1, Number(res.data.step))));
          }
        } catch (_) {}
        return;
      }

      // 5) Default: START FRESH. Per spec, clicking "Lisa töökuulutus"
      //    must always reset previous state. The draft is still safely
      //    saved on the server (autosaved as user types) and can be
      //    resumed later via ?resume=1 or a future drafts-list UI.
      // (Nothing to do — form stays at EMPTY_FORM.)
    })();
  }, [loading, user, needsCompany, searchParams]);

  const resumePendingDraft = useCallback(() => {
    if (!pendingDraft) return;
    setDraftId(pendingDraft.draft_id);
    if (pendingDraft.data) setForm((f) => ({ ...f, ...pendingDraft.data }));
    if (pendingDraft.step) setStep(Math.min(totalSteps, Math.max(1, Number(pendingDraft.step))));
    setPendingDraft(null);
  }, [pendingDraft]);

  const discardPendingDraft = useCallback(async () => {
    if (!pendingDraft) return;
    try {
      await api.delete(`/jobs/drafts/${pendingDraft.draft_id}`);
    } catch (_) {}
    // Reset the form to a pristine EMPTY_FORM — critical to prevent state
    // from a previous occupation leaking into the new job.
    setForm(EMPTY_FORM);
    setStep(1);
    setDraftId(null);
    setPendingDraft(null);
  }, [pendingDraft]);

  // ---------- Autosave (debounced) ----------
  const scheduleSave = useCallback((nextForm, nextStep) => {
    if (!user || needsCompany) return;
    setAutosave('saving');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        const res = await api.post('/jobs/drafts', {
          draft_id: draftId,
          step: nextStep,
          data: nextForm,
        });
        if (res.data?.draft_id) setDraftId(res.data.draft_id);
        setAutosave('saved');
      } catch (err) {
        setAutosave('failed');
      }
    }, 900);
  }, [draftId, user, needsCompany]);

  const update = useCallback((patch) => {
    setForm((prev) => {
      const next = typeof patch === 'function' ? patch(prev) : { ...prev, ...patch };
      scheduleSave(next, step);
      return next;
    });
  }, [scheduleSave, step]);

  const goToStep = (n) => {
    const target = Math.min(totalSteps, Math.max(1, n));
    setStep(target);
    scheduleSave(form, target);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ---------- Validation ----------
  const validateStep = (n) => {
    const e = {};
    if (n >= 1) {
      if (!form.title || form.title.trim().length < 5) e.title = t('jobWizard.errors.title');
      if (!form.category) e.category = t('jobWizard.errors.category');
      if (!form.who_type) e.who_type = t('jobWizard.errors.who');
      // New flexible workforce validation
      if (['multiple_workers', 'team', 'subcontractor'].includes(form.who_type)) {
        if (form.workforce_amount_type === 'exact') {
          const n = Number(form.workforce_exact_amount);
          if (!Number.isInteger(n) || n < 1) e.workforce_exact = t('jobWizard.errors.workforceExact');
        } else if (form.workforce_amount_type === 'range') {
          const mn = Number(form.workforce_minimum);
          const mx = Number(form.workforce_maximum);
          if (!Number.isInteger(mn) || mn < 1 || !Number.isInteger(mx) || mx < 1) {
            e.workforce_range = t('jobWizard.errors.workforceRangeMissing');
          } else if (mx < mn) {
            e.workforce_range = t('jobWizard.errors.workforceRangeInverted');
          }
        }
      }
      if (!form.description || form.description.trim().length < 20) e.description = t('jobWizard.errors.description');
    }
    if (n >= 2) {
      if (!form.country) e.country = t('jobWizard.errors.country');
      if (!form.city) e.city = t('jobWizard.errors.city');
    }
    if (n >= 3) {
      if (!form.start_type) e.start_type = t('jobWizard.errors.start');
      if (!form.duration) e.duration = t('jobWizard.errors.duration');
      if (!form.cooperation_type) e.cooperation_type = t('jobWizard.errors.cooperation');
      if (form.salary_type !== 'agreed') {
        if (!form.salary_min) e.salary = t('jobWizard.errors.salary');
        if (form.salary_min && form.salary_max && Number(form.salary_max) < Number(form.salary_min)) {
          e.salary = t('jobWizard.errors.salaryRange');
        }
      }
    }
    if (n >= 4) {
      if (!form.application_method) e.application_method = t('jobWizard.errors.applicationMethod');
      if (!form.contact_person) e.contact_person = t('jobWizard.errors.contactPerson');
    }
    return e;
  };

  const validateAll = () => {
    const e = validateStep(4);
    setErrors(e);
    return { ok: Object.keys(e).length === 0, errs: e };
  };

  const attemptNext = () => {
    const e = validateStep(step);
    setErrors(e);
    if (Object.keys(e).length > 0) {
      toast.error(t('jobWizard.errors.fixToContinue'));
      return;
    }
    goToStep(step + 1);
  };

  // ---------- Publish ----------
  const doPublish = async () => {
    const { ok, errs } = validateAll();
    if (!ok) {
      // return to first invalid step
      const stepOf = {
        title: 1, category: 1, who_type: 1, description: 1,
        country: 2, city: 2,
        start_type: 3, duration: 3, cooperation_type: 3, salary: 3,
        application_method: 4, contact_person: 4,
      };
      const firstKey = Object.keys(errs)[0];
      const targetStep = stepOf[firstKey] || 1;
      goToStep(targetStep);
      toast.error(t('jobWizard.errors.fixBeforePublish'));
      setShowConfirm(false);
      return;
    }
    setPublishing(true);
    try {
      // Map form → JobCreate payload
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category,
        job_type: form.cooperation_type === 'employment' ? 'full_time'
                 : form.cooperation_type === 'fixed_term' ? 'contract'
                 : form.cooperation_type === 'subcontract' ? 'subcontract'
                 : form.cooperation_type === 'project' ? 'contract'
                 : 'contract',
        duration: form.duration,
        city: form.city.trim(),
        country: form.country,
        radius_km: 50,
        salary_type: form.salary_type === 'agreed' ? 'negotiable'
                    : form.salary_type === 'hourly' ? 'hourly'
                    : form.salary_type === 'monthly' ? 'fixed'
                    : form.salary_type === 'project' ? 'fixed' : 'negotiable',
        salary_min: form.salary_min ? Number(form.salary_min) : null,
        salary_max: form.salary_max ? Number(form.salary_max) : null,
        salary_currency: form.salary_currency || 'EUR',
        language_required: form.languages[0]?.code || null,
        language_level: form.languages[0]?.level || null,
        skills_required: form.skills.map((s) => s.name),
        experience_years_min: form.experience === '1_year' ? 1
                             : form.experience === '3_years' ? 3
                             : form.experience === '5_years' ? 5 : 0,
        allow_guest_apply: form.application_method !== 'keikka_only',
        allow_direct_contact: form.application_method !== 'keikka_only',
        auto_notify_matches: false,
        publish_type: 'public',
        wizard_data: form,
        who_type: form.who_type,
        worker_count: Number(form.workforce_exact_amount) || Number(form.worker_count) || null,
        workforce_amount_type: form.workforce_amount_type,
        workforce_exact_amount: form.workforce_exact_amount ? Number(form.workforce_exact_amount) : null,
        workforce_minimum: form.workforce_minimum ? Number(form.workforce_minimum) : null,
        workforce_maximum: form.workforce_maximum ? Number(form.workforce_maximum) : null,
        show_workforce_amount: !!form.show_workforce_amount,
        languages_required: form.languages,
        required_language_logic: form.required_language_logic || 'any',
        team_language_requirement: form.team_language_requirement || 'at_least_one_member',
        skills_required_details: form.skills,
        address_line: form.address_line || null,
        address_visibility: form.address_visibility,
        work_arrangement: form.work_arrangement || null,
        travel_between_sites: form.travel_between_sites || null,
        start_type: form.start_type,
        start_date: form.start_date || null,
        cooperation_type: form.cooperation_type,
        hours_per_day: form.hours_per_day ? Number(form.hours_per_day) : null,
        days_per_week: form.days_per_week ? Number(form.days_per_week) : null,
        hours_per_week: form.hours_per_week ? Number(form.hours_per_week) : null,
        overtime_available: !!form.overtime_available,
        weekend_work: !!form.weekend_work,
        night_work: !!form.night_work,
        rotation_type: form.rotation_type || null,
        rotation_pattern: form.rotation_pattern || null,
        salary_period: form.salary_type,
        salary_gross_net: form.salary_gross_net,
        accommodation_type: form.accommodation_type || null,
        accommodation_cost: form.accommodation_cost ? Number(form.accommodation_cost) : null,
        transport_to_country: form.transport_to_country || null,
        transport_local: form.transport_local || null,
        equipment_provided: form.equipment_provided,
        equipment_worker_own: form.equipment_worker_own,
        benefits: form.benefits,
        application_method: form.application_method,
        contact_email: form.contact_email || null,
        contact_phone: form.contact_phone || null,
        contact_person: form.contact_person,
        screening_questions: form.screening_questions,
        draft_id: draftId, // idempotent publish
      };
      const res = await api.post('/jobs', payload);
      const jobId = res.data?.id;
      setShowConfirm(false);
      if (jobId) {
        navigate(`/jobs/create/success?jobId=${jobId}`, { replace: true });
      } else {
        toast.error(t('common.error'));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('common.error'));
    } finally {
      setPublishing(false);
    }
  };

  // ---------- Render ----------
  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  if (needsCompany) {
    return (
      <div className="min-h-screen bg-slate-50">
        <GlobalNav />
        <div className="max-w-4xl mx-auto px-4 py-10">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-slate-900" data-testid="wizard-heading-bootstrap">
              {t('jobWizard.bootstrap.pageTitle')}
            </h1>
            <p className="text-slate-600 mt-2">
              {t('jobWizard.bootstrap.pageSubtitle')}
            </p>
          </div>
          <CompanyBootstrap onDone={() => window.location.reload()} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <GlobalNav />
      <div className="max-w-7xl mx-auto px-4 py-6 md:py-10">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" data-testid="wizard-title">
              {t('jobWizard.pageTitle')}
            </h1>
            <p className="text-slate-600 mt-1">
              {t('jobWizard.pageSubtitle')}
            </p>
          </div>
          <AutosaveBadge status={autosave} t={t} />
        </div>

        {/* Step dots */}
        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="wizard-progress">
          <StepDot n={1} label={t('jobWizard.steps.job')} active={step === 1} done={step > 1} onClick={() => goToStep(1)} />
          <StepDot n={2} label={t('jobWizard.steps.location')} active={step === 2} done={step > 2} onClick={() => (step >= 2 || Object.keys(validateStep(1)).length === 0) && goToStep(2)} />
          <StepDot n={3} label={t('jobWizard.steps.terms')} active={step === 3} done={step > 3} onClick={() => (step >= 3 || Object.keys(validateStep(2)).length === 0) && goToStep(3)} />
          <StepDot n={4} label={t('jobWizard.steps.review')} active={step === 4} done={step > 4} onClick={() => (step >= 4 || Object.keys(validateStep(3)).length === 0) && goToStep(4)} />
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Form */}
          <div className="lg:col-span-8">
            <Card>
              <CardContent className="pt-6">
                {step === 1 && <Step1Job f={form} up={update} errors={errors} t={t} uiLang={language} />}
                {step === 2 && <Step2Location f={form} up={update} errors={errors} t={t} />}
                {step === 3 && <Step3Terms f={form} up={update} errors={errors} t={t} />}
                {step === 4 && <Step4Review f={form} up={update} errors={errors} t={t} onEdit={goToStep} />}

                {/* Nav buttons */}
                <div className="flex flex-wrap items-center justify-between gap-3 mt-8 pt-6 border-t border-slate-100">
                  <div className="flex flex-wrap items-center gap-2">
                    {step > 1 && (
                      <Button variant="outline" size="sm" onClick={() => goToStep(step - 1)} data-testid="wiz-back">
                        <ArrowLeft className="h-4 w-4 mr-2" /> {t('jobWizard.back')}
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => scheduleSave(form, step)} className="text-slate-600" data-testid="wiz-save-draft">
                      <Save className="h-4 w-4 mr-2" /> {t('jobWizard.saveDraft')}
                    </Button>
                  </div>
                  {step < 4 ? (
                    <Button onClick={attemptNext} data-testid="wiz-next">
                      {t('jobWizard.next')} <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  ) : (
                    <Button onClick={() => {
                      const { ok } = validateAll();
                      if (!ok) { toast.error(t('jobWizard.errors.fixBeforePublish')); return; }
                      setShowConfirm(true);
                    }} data-testid="wiz-publish">
                      <ShieldCheck className="h-4 w-4 mr-2" /> {t('jobWizard.publish')}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sticky preview (desktop) */}
          <aside className="hidden lg:block lg:col-span-4">
            <div className="sticky top-6">
              <LivePreview f={form} t={t} />
              <p className="text-[11px] text-slate-400 mt-2 text-center">
                {t('jobWizard.preview.hint')}
              </p>
            </div>
          </aside>
        </div>
      </div>

      {/* Mobile floating Preview button — always accessible on < lg viewports.
          Sits above the platform MobileBottomNav (z-50, ~64px tall). */}
      <div className="lg:hidden fixed right-4 z-[60]" style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 88px)' }}>
        <Button
          onClick={() => setShowPreviewSheet(true)}
          className="shadow-lg rounded-full h-12 px-5"
          data-testid="wiz-preview-mobile"
        >
          <Eye className="h-4 w-4 mr-2" /> {t('jobWizard.previewBtn')}
        </Button>
      </div>

      {/* Mobile preview sheet */}
      <Sheet open={showPreviewSheet} onOpenChange={setShowPreviewSheet}>
        <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto" data-testid="preview-sheet">
          <SheetHeader>
            <SheetTitle>{t('jobWizard.preview.title')}</SheetTitle>
          </SheetHeader>
          <div className="pt-4">
            <LivePreview f={form} t={t} />
          </div>
        </SheetContent>
      </Sheet>

      <PublishConfirm open={showConfirm} publishing={publishing}
                      onCancel={() => setShowConfirm(false)} onConfirm={doPublish} />

      {/* Resume-previous-draft dialog. Never silently loads the draft — the
          employer must click "Continue" for it to hydrate the form. */}
      <Dialog open={!!pendingDraft} onOpenChange={(v) => { if (!v) discardPendingDraft(); }}>
        <DialogContent data-testid="wiz-resume-dialog">
          <DialogHeader>
            <DialogTitle>
              {t('jobWizard.resume.title') !== 'jobWizard.resume.title'
                ? t('jobWizard.resume.title')
                : 'Jätka pooleliolevat mustandit?'}
            </DialogTitle>
            <DialogDescription>
              {t('jobWizard.resume.body') !== 'jobWizard.resume.body'
                ? t('jobWizard.resume.body')
                : 'Leidsin salvestatud pooleliolevad andmed. Kas soovid jätkata sealt, kus pooleli jäi, või alustada täiesti uut kuulutust?'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={discardPendingDraft} data-testid="wiz-resume-discard">
              {t('jobWizard.resume.startNew') !== 'jobWizard.resume.startNew'
                ? t('jobWizard.resume.startNew')
                : 'Alusta uut'}
            </Button>
            <Button onClick={resumePendingDraft} data-testid="wiz-resume-continue">
              {t('jobWizard.resume.continue') !== 'jobWizard.resume.continue'
                ? t('jobWizard.resume.continue')
                : 'Jätka mustandit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- Flexible workforce count control (exact/range/ongoing/undecided) ----------

const WorkforceControl = ({ f, up, errors, t }) => {
  // Prefix for the question label + team vs individual wording
  const scope = f.who_type === 'team' ? 'team'
              : f.who_type === 'subcontractor' ? 'sub'
              : 'individual';
  const questionKey = `jobWizard.workforce.q_${scope}`;
  const modes = [
    { v: 'exact',     l: t('jobWizard.workforce.mode.exact') },
    { v: 'range',     l: t('jobWizard.workforce.mode.range') },
    { v: 'ongoing',   l: t('jobWizard.workforce.mode.ongoing') },
    { v: 'undecided', l: t('jobWizard.workforce.mode.undecided') },
  ];
  return (
    <div className="mt-4 p-4 rounded-lg border border-slate-200 bg-slate-50/40">
      <div className="text-sm font-semibold text-slate-800">{t(questionKey)}</div>
      <p className="text-xs text-slate-500 mt-1">{t('jobWizard.workforce.help')}</p>
      <div className="grid sm:grid-cols-2 gap-2 mt-3" data-testid="f-workforce-mode">
        {modes.map((m) => (
          <button key={m.v} type="button"
                  onClick={() => up({ workforce_amount_type: m.v })}
                  className={`text-left p-3 rounded-lg border-2 text-sm ${f.workforce_amount_type === m.v ? 'border-brand bg-orange-50' : 'border-slate-200 bg-white'}`}
                  data-testid={`f-workforce-mode-${m.v}`}>
            {m.l}
          </button>
        ))}
      </div>

      {f.workforce_amount_type === 'exact' && (
        <div className="mt-3">
          <Label>{t('jobWizard.workforce.exactLabel')}</Label>
          <Input
            type="number"
            min={1}
            step={1}
            value={f.workforce_exact_amount}
            onChange={(e) => {
              const raw = e.target.value;
              // Allow empty string temporarily so user can clear
              if (raw === '') { up({ workforce_exact_amount: '' }); return; }
              const n = parseInt(raw, 10);
              if (Number.isNaN(n)) return;
              up({ workforce_exact_amount: Math.max(1, n), worker_count: Math.max(1, n) });
            }}
            data-testid="f-workforce-exact"
          />
          {errors.workforce_exact && <p className="text-xs text-red-600 mt-1">{errors.workforce_exact}</p>}
        </div>
      )}

      {f.workforce_amount_type === 'range' && (
        <div className="mt-3 grid sm:grid-cols-2 gap-3">
          <div>
            <Label>{t('jobWizard.workforce.rangeMin')}</Label>
            <Input type="number" min={1} step={1} value={f.workforce_minimum}
                   onChange={(e) => up({ workforce_minimum: e.target.value })}
                   data-testid="f-workforce-min" />
          </div>
          <div>
            <Label>{t('jobWizard.workforce.rangeMax')}</Label>
            <Input type="number" min={1} step={1} value={f.workforce_maximum}
                   onChange={(e) => up({ workforce_maximum: e.target.value })}
                   data-testid="f-workforce-max" />
          </div>
          {errors.workforce_range && <p className="text-xs text-red-600 mt-1 sm:col-span-2">{errors.workforce_range}</p>}
        </div>
      )}

      {(f.workforce_amount_type === 'exact' || f.workforce_amount_type === 'range') && (
        <label className="flex items-start gap-2 mt-4 text-sm text-slate-700">
          <Checkbox
            checked={!!f.show_workforce_amount}
            onCheckedChange={(v) => up({ show_workforce_amount: !!v })}
            data-testid="f-workforce-show"
          />
          <span>
            <span className="font-medium">{t('jobWizard.workforce.showLabel')}</span>
            <span className="block text-xs text-slate-500 mt-0.5">{t('jobWizard.workforce.showHelp')}</span>
          </span>
        </label>
      )}
    </div>
  );
};


// ---------- Step 1: Töö ----------

/**
 * Small controlled input + button that lets employers add a free-text
 * skill or certificate that isn't in the taxonomy. Custom entries get an
 * `id` prefixed with `custom:` so they can be distinguished on save.
 */
const ManualSkillAdder = ({ existingIds, onAdd, t }) => {
  const [value, setValue] = React.useState('');
  const submit = () => {
    const v = value.trim();
    if (!v) return;
    const id = `custom:${v.toLowerCase().replace(/\s+/g, '-')}`;
    if (existingIds.includes(id)) { setValue(''); return; }
    onAdd(v);
    setValue('');
  };
  return (
    <div className="mt-3 flex gap-2" data-testid="wiz-skills-manual">
      <Input
        value={value}
        placeholder={t('jobWizard.step1.addOwnSkill') !== 'jobWizard.step1.addOwnSkill'
          ? t('jobWizard.step1.addOwnSkill')
          : 'Lisa muu oskus või tunnistus'}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
        className="h-9 text-sm"
        data-testid="wiz-skills-manual-input"
      />
      <Button
        type="button"
        variant="outline"
        onClick={submit}
        disabled={!value.trim()}
        data-testid="wiz-skills-manual-add"
      >
        {t('jobWizard.step1.addBtn') !== 'jobWizard.step1.addBtn'
          ? t('jobWizard.step1.addBtn')
          : 'Lisa'}
      </Button>
    </div>
  );
};

/**
 * Fetch AI + taxonomy-based skill/certificate suggestions for the current
 * job draft. Debounced ~700ms, cancels stale requests via AbortController
 * so a later title doesn't get overwritten by an older LLM response.
 */
const useSkillSuggestions = ({ title, description, category, country, uiLang, selectedIds }) => {
  const [state, setState] = React.useState({
    loading: false,
    occupation: null,
    suggestions: [],
    source: null,
  });
  const abortRef = React.useRef(null);
  const timerRef = React.useRef(null);

  React.useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (abortRef.current) abortRef.current.abort();

    const cleanTitle = (title || '').trim();
    if (cleanTitle.length < 3) {
      setState({ loading: false, occupation: null, suggestions: [], source: null });
      return;
    }

    setState((s) => ({ ...s, loading: true }));
    timerRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const res = await api.post(
          '/jobs/skill-suggestions',
          {
            title: cleanTitle,
            description: description || '',
            category: category || null,
            country: country || null,
            ui_lang: uiLang || 'et',
            selected_ids: selectedIds || [],
          },
          { signal: controller.signal }
        );
        // Guard: if a newer request has already started, ignore this response.
        if (abortRef.current !== controller) return;
        setState({
          loading: false,
          occupation: res.data?.occupation || null,
          suggestions: res.data?.suggestions || [],
          source: res.data?.source || 'taxonomy',
        });
      } catch (err) {
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
        setState({ loading: false, occupation: null, suggestions: [], source: null });
      }
    }, 700);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
    // Note: selectedIds intentionally NOT a dep — we don't want to refetch
    // every time the user clicks a chip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, category, country, uiLang]);

  return state;
};

const Step1Job = ({ f, up, errors, t, uiLang }) => {
  // Skill helpers: skills are {id, name, type, requirement: 'required'|'preferred'}.
  // Legacy drafts may store {name, required} — we treat those as 'required'.
  const normalizedSkills = React.useMemo(() => (f.skills || []).map((s) => ({
    id: s.id || s.name,
    name: s.name,
    type: s.type || 'skill',
    requirement: s.requirement || (s.required === false ? 'preferred' : 'required'),
  })), [f.skills]);

  const selectedIds = React.useMemo(() => normalizedSkills.map((s) => s.id), [normalizedSkills]);

  const suggestions = useSkillSuggestions({
    title: f.title,
    description: f.description,
    category: f.category,
    country: f.country,
    uiLang,
    selectedIds,
  });

  // Auto-suggest the category on first occupation detection ONLY when the
  // employer hasn't already picked one. Never override a manual choice.
  React.useEffect(() => {
    if (!suggestions.occupation) return;
    if (f.category) return;
    const catId = suggestions.occupation.category;
    // Map taxonomy category → CATEGORIES list where possible
    const catMap = {
      cleaning: 'other',
      construction: 'construction',
      transport: 'transport',
      logistics: 'logistics',
      food_service: 'other',
      care: 'other',
    };
    const suggested = catMap[catId];
    if (suggested) up({ category: suggested });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestions.occupation?.id]);

  const addSuggestion = (sug) => {
    if (selectedIds.includes(sug.id)) return;
    up({
      skills: [
        ...normalizedSkills,
        { id: sug.id, name: sug.name, type: sug.type, requirement: 'required' },
      ],
    });
  };
  const removeSkill = (id) => {
    up({ skills: normalizedSkills.filter((s) => s.id !== id) });
  };
  const setSkillReq = (id, requirement) => {
    up({ skills: normalizedSkills.map((s) => s.id === id ? { ...s, requirement } : s) });
  };

  const addLang = (code) => {
    if (f.languages.find((l) => l.code === code)) return;
    // Default new languages to 'working' level + 'required'
    up({ languages: [...f.languages, { code, level: 'working', requirement: 'required' }] });
  };
  const removeLang = (code) => up({ languages: f.languages.filter((l) => l.code !== code) });
  const setLangLvl = (code, level) => up({ languages: f.languages.map((l) => l.code === code ? { ...l, level } : l) });
  const setLangReq = (code, requirement) => up({ languages: f.languages.map((l) => l.code === code ? { ...l, requirement } : l) });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-slate-900">{t('jobWizard.step1.heading')}</h2>
        <p className="text-sm text-slate-600 mt-1">{t('jobWizard.step1.sub')}</p>
      </div>

      <div>
        <Label>{t('jobWizard.step1.titleLabel')} *</Label>
        <Input value={f.title} onChange={(e) => up({ title: e.target.value })}
               placeholder={t('jobWizard.step1.titlePlaceholder')}
               data-testid="f-title" />
        <p className="text-xs text-slate-500 mt-1">{t('jobWizard.step1.titleHelper')}</p>
        {errors.title && <p className="text-xs text-red-600 mt-1">{errors.title}</p>}
      </div>

      <div>
        <Label>{t('jobWizard.step1.categoryLabel')} *</Label>
        <Select value={f.category} onValueChange={(v) => up({ category: v })}>
          <SelectTrigger data-testid="f-category"><SelectValue placeholder={t('jobWizard.step1.categoryPlaceholder')} /></SelectTrigger>
          <SelectContent>
            {CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {errors.category && <p className="text-xs text-red-600 mt-1">{errors.category}</p>}
      </div>

      <div>
        <Label>{t('jobWizard.step1.whoLabel')} *</Label>
        <CardSelect
          testid="f-who"
          value={f.who_type}
          onChange={(v) => up({ who_type: v })}
          options={[
            { value: 'single_worker', label: t('jobWizard.opts.who.single_worker'), icon: <UserIcon className="h-4 w-4" /> },
            { value: 'multiple_workers', label: t('jobWizard.opts.who.multiple_workers'), icon: <Users className="h-4 w-4" /> },
            { value: 'team', label: t('jobWizard.opts.who.team'), icon: <UserPlus className="h-4 w-4" /> },
            { value: 'subcontractor', label: t('jobWizard.opts.who.subcontractor'), icon: <Building2 className="h-4 w-4" /> },
          ]}
        />
        {errors.who_type && <p className="text-xs text-red-600 mt-1">{errors.who_type}</p>}
        {(f.who_type === 'multiple_workers' || f.who_type === 'team' || f.who_type === 'subcontractor') && (
          <WorkforceControl f={f} up={up} errors={errors} t={t} />
        )}
        {f.who_type === 'team' && (
          <div className="mt-3 grid sm:grid-cols-2 gap-2">
            {[
              { k: 'own_foreman', l: t('jobWizard.step1.team.foreman') },
              { k: 'own_vehicle', l: t('jobWizard.step1.team.vehicle') },
              { k: 'own_tools',   l: t('jobWizard.step1.team.tools') },
            ].map((opt) => (
              <label key={opt.k} className="inline-flex items-center gap-2 text-sm text-slate-700 p-3 border rounded-lg bg-white">
                <Checkbox
                  checked={!!f.team_options[opt.k]}
                  onCheckedChange={(v) => up({ team_options: { ...f.team_options, [opt.k]: !!v } })}
                  data-testid={`f-team-${opt.k}`}
                />
                {opt.l}
              </label>
            ))}
          </div>
        )}
      </div>

      <div>
        <Label>{t('jobWizard.step1.descLabel')} *</Label>
        <Textarea
          rows={6}
          maxLength={2500}
          placeholder={t('jobWizard.step1.descPlaceholder')}
          value={f.description}
          onChange={(e) => up({ description: e.target.value })}
          data-testid="f-description"
        />
        <div className="flex justify-between text-xs text-slate-500 mt-1">
          <span>{(f.description || '').length} / 2500</span>
          {errors.description && <span className="text-red-600">{errors.description}</span>}
        </div>
      </div>

      <div>
        <Label>{t('jobWizard.step1.expLabel')}</Label>
        <Select value={f.experience} onValueChange={(v) => up({ experience: v })}>
          <SelectTrigger data-testid="f-experience"><SelectValue placeholder={t('jobWizard.step1.expPlaceholder')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">{t('jobWizard.opts.exp.none')}</SelectItem>
            <SelectItem value="1_year">{t('jobWizard.opts.exp.1_year')}</SelectItem>
            <SelectItem value="3_years">{t('jobWizard.opts.exp.3_years')}</SelectItem>
            <SelectItem value="5_years">{t('jobWizard.opts.exp.5_years')}</SelectItem>
            <SelectItem value="other">{t('jobWizard.opts.exp.other')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>{t('jobWizard.step1.skillsLabel')}</Label>

        {/* Selected skills — brand-orange chips with Required / Preferred toggles */}
        {normalizedSkills.length > 0 && (
          <div className="mt-2 space-y-1" data-testid="wiz-skills-selected">
            {normalizedSkills.map((s) => (
              <div key={s.id} className="flex items-center justify-between text-xs bg-orange-50 border border-orange-200 rounded px-3 py-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-slate-800 font-medium truncate">{s.name}</span>
                  {s.type === 'certificate' && (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-orange-300 text-orange-700 shrink-0">
                      {t('jobWizard.step1.certBadge') !== 'jobWizard.step1.certBadge' ? t('jobWizard.step1.certBadge') : 'tunnistus'}
                    </Badge>
                  )}
                </div>
                <div className="flex gap-1 items-center">
                  <button
                    type="button"
                    onClick={() => setSkillReq(s.id, 'required')}
                    className={`px-2 py-0.5 rounded ${s.requirement === 'required' ? 'bg-brand text-white' : 'bg-white border'}`}
                    data-testid={`wiz-skill-req-${s.id}`}
                  >
                    {t('jobWizard.opts.skill.required')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setSkillReq(s.id, 'preferred')}
                    className={`px-2 py-0.5 rounded ${s.requirement === 'preferred' ? 'bg-brand text-white' : 'bg-white border'}`}
                    data-testid={`wiz-skill-pref-${s.id}`}
                  >
                    {t('jobWizard.opts.skill.plus')}
                  </button>
                  <button
                    type="button"
                    onClick={() => removeSkill(s.id)}
                    className="ml-1 h-6 w-6 grid place-items-center rounded hover:bg-orange-100 text-orange-700"
                    aria-label="remove"
                    data-testid={`wiz-skill-remove-${s.id}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* AI recommendations — grey chips, only appended when clicked */}
        <div className="mt-3 rounded-lg border border-dashed border-slate-300 bg-slate-50/60 p-3" data-testid="wiz-skills-suggestions-block">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold text-slate-700">
              {t('jobWizard.step1.aiSuggestTitle') !== 'jobWizard.step1.aiSuggestTitle'
                ? t('jobWizard.step1.aiSuggestTitle')
                : 'AI soovitused'}
            </div>
            {suggestions.loading && (
              <span className="inline-flex items-center gap-1 text-[11px] text-slate-500" data-testid="wiz-skills-loading">
                <Loader2 className="h-3 w-3 animate-spin" />
                {t('jobWizard.step1.aiSuggestLoading') !== 'jobWizard.step1.aiSuggestLoading'
                  ? t('jobWizard.step1.aiSuggestLoading')
                  : 'Analüüsin ametit…'}
              </span>
            )}
          </div>
          <p className="text-[11px] text-slate-500 mb-2">
            {t('jobWizard.step1.aiSuggestHelp') !== 'jobWizard.step1.aiSuggestHelp'
              ? t('jobWizard.step1.aiSuggestHelp')
              : 'Soovitused töö nimetuse ja kirjelduse põhjal — vajuta lisamiseks.'}
          </p>

          {(!suggestions.suggestions || suggestions.suggestions.length === 0) && !suggestions.loading && (
            <div className="text-[11px] text-slate-500 italic" data-testid="wiz-skills-empty">
              {(f.title || '').trim().length < 3
                ? (t('jobWizard.step1.aiSuggestPrompt') !== 'jobWizard.step1.aiSuggestPrompt'
                    ? t('jobWizard.step1.aiSuggestPrompt')
                    : 'Sisesta ametinimetus, et saada soovitusi.')
                : (t('jobWizard.step1.aiSuggestNone') !== 'jobWizard.step1.aiSuggestNone'
                    ? t('jobWizard.step1.aiSuggestNone')
                    : 'Selle ametinimetuse jaoks soovitusi ei leitud — lisa oskused käsitsi allpool.')}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {(suggestions.suggestions || [])
              .filter((s) => !selectedIds.includes(s.id))
              .map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => addSuggestion(s)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs bg-white text-slate-700 border border-slate-300 hover:border-brand hover:bg-orange-50 transition"
                  data-testid={`wiz-skill-suggest-${s.id}`}
                  title={s.type === 'certificate' ? 'tunnistus / certificate' : 'oskus / skill'}
                >
                  <span>+</span>
                  <span>{s.name}</span>
                  {s.type === 'certificate' && (
                    <span className="text-[9px] uppercase tracking-wider text-slate-400">·crt</span>
                  )}
                </button>
              ))}
          </div>
        </div>

        {/* Manual add — free text field */}
        <ManualSkillAdder
          existingIds={selectedIds}
          onAdd={(name) => up({
            skills: [
              ...normalizedSkills,
              { id: `custom:${name.toLowerCase().replace(/\s+/g, '-')}`, name, type: 'skill', requirement: 'required' },
            ],
          })}
          t={t}
        />
      </div>

      <div>
        <Label>{t('jobWizard.step1.langLabel')}</Label>
        <div className="flex flex-wrap gap-2 mt-1">
          {LANGUAGES.map((l) => {
            const sel = !!f.languages.find((x) => x.code === l.code);
            return (
              <button key={l.code} type="button"
                      onClick={() => sel ? removeLang(l.code) : addLang(l.code)}
                      className={`px-3 py-1.5 rounded-full text-xs border transition
                        ${sel ? 'bg-brand text-white border-brand' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'}`}
                      data-testid={`f-lang-${l.code}`}>
                {l.label}
              </button>
            );
          })}
        </div>
        {f.languages.length > 0 && (
          <div className="mt-3 space-y-2">
            {f.languages.map((l) => {
              // Back-compat: legacy drafts may not have `requirement` — treat as 'required'
              const req = l.requirement || 'required';
              return (
                <div key={l.code} className="flex flex-wrap items-center gap-2 text-xs bg-slate-50 rounded px-3 py-2">
                  <span className="text-slate-700 w-16 font-medium">{LANGUAGES.find((x) => x.code === l.code)?.label}</span>
                  <Select value={l.level} onValueChange={(v) => setLangLvl(l.code, v)}>
                    <SelectTrigger className="h-8 w-40 text-xs" data-testid={`f-lang-level-${l.code}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LANG_LEVELS.map((lv) => <SelectItem key={lv} value={lv}>{t(`jobWizard.opts.lang.${lv}`)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-1 ml-auto">
                    <button type="button" onClick={() => setLangReq(l.code, 'required')}
                            className={`px-2 py-1 rounded ${req === 'required' ? 'bg-brand text-white' : 'bg-white border border-slate-200 text-slate-600'}`}
                            data-testid={`f-lang-req-${l.code}`}>
                      {t('jobWizard.opts.skill.required')}
                    </button>
                    <button type="button" onClick={() => setLangReq(l.code, 'preferred')}
                            className={`px-2 py-1 rounded ${req === 'preferred' ? 'bg-brand text-white' : 'bg-white border border-slate-200 text-slate-600'}`}
                            data-testid={`f-lang-pref-${l.code}`}>
                      {t('jobWizard.opts.skill.plus')}
                    </button>
                  </div>
                </div>
              );
            })}

            {/* Required-language logic — only when 2+ languages are marked as required */}
            {(f.languages.filter((l) => (l.requirement || 'required') === 'required').length >= 2) && (
              <div className="mt-3 p-3 rounded-lg border border-slate-200 bg-white" data-testid="f-lang-logic-block">
                <div className="text-xs font-semibold text-slate-800">{t('jobWizard.step1.langLogicQ')}</div>
                <p className="text-[11px] text-slate-500 mt-1">{t('jobWizard.step1.langLogicHelp')}</p>
                <div className="grid sm:grid-cols-2 gap-2 mt-2">
                  {['any', 'all'].map((v) => (
                    <button key={v} type="button" onClick={() => up({ required_language_logic: v })}
                            className={`text-left p-2 rounded border-2 text-xs ${f.required_language_logic === v ? 'border-brand bg-orange-50' : 'border-slate-200 bg-white'}`}
                            data-testid={`f-lang-logic-${v}`}>
                      {t(`jobWizard.opts.langLogic.${v}`)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Team / subcontractor: at-least-one-member toggle */}
            {(f.who_type === 'team' || f.who_type === 'subcontractor') &&
             f.languages.some((l) => (l.requirement || 'required') === 'required') && (
              <label className="flex items-start gap-2 mt-3 text-sm text-slate-700 p-3 border rounded-lg bg-white" data-testid="f-team-lang-block">
                <Checkbox
                  checked={f.team_language_requirement === 'at_least_one_member'}
                  onCheckedChange={(v) => up({ team_language_requirement: v ? 'at_least_one_member' : 'every_member' })}
                  data-testid="f-team-lang-toggle"
                />
                <span>
                  <span className="font-medium">{t('jobWizard.step1.teamLangLabel')}</span>
                  <span className="block text-xs text-slate-500 mt-0.5">{t('jobWizard.step1.teamLangHelp')}</span>
                </span>
              </label>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ---------- Step 2: Asukoht ----------

const Step2Location = ({ f, up, errors, t }) => (
  <div className="space-y-6">
    <div>
      <h2 className="text-xl font-bold text-slate-900">{t('jobWizard.step2.heading')}</h2>
    </div>
    <div className="grid sm:grid-cols-2 gap-4">
      <div>
        <Label>{t('jobWizard.step2.countryLabel')} *</Label>
        <Select value={f.country} onValueChange={(v) => up({ country: v })}>
          <SelectTrigger data-testid="f-country"><SelectValue placeholder={t('jobWizard.step2.countryPlaceholder')} /></SelectTrigger>
          <SelectContent>
            {COUNTRIES.map((c) => <SelectItem key={c.code} value={c.code}>{countryLabel(c.code, t)}</SelectItem>)}
          </SelectContent>
        </Select>
        {errors.country && <p className="text-xs text-red-600 mt-1">{errors.country}</p>}
      </div>
      <div>
        <Label>{t('jobWizard.step2.cityLabel')} *</Label>
        <Input value={f.city} onChange={(e) => up({ city: e.target.value })}
               placeholder="Espoo, Stockholm, Oslo…" data-testid="f-city" />
        {errors.city && <p className="text-xs text-red-600 mt-1">{errors.city}</p>}
      </div>
    </div>

    <div>
      <Label>{t('jobWizard.step2.addressLabel')}</Label>
      <Input value={f.address_line} onChange={(e) => up({ address_line: e.target.value })}
             placeholder={t('jobWizard.step2.addressPlaceholder')} data-testid="f-address" />
      <div className="flex gap-2 mt-2">
        {[
          { v: 'candidates_only', l: t('jobWizard.opts.visibility.candidates_only') },
          { v: 'public',          l: t('jobWizard.opts.visibility.public') },
        ].map((opt) => (
          <button key={opt.v} type="button" onClick={() => up({ address_visibility: opt.v })}
                  className={`text-xs px-3 py-1.5 rounded-full border ${f.address_visibility === opt.v ? 'bg-brand text-white border-brand' : 'bg-white border-slate-200'}`}
                  data-testid={`f-address-visibility-${opt.v}`}>
            {opt.l}
          </button>
        ))}
      </div>
    </div>

    <div>
      <Label>{t('jobWizard.step2.arrangementLabel')}</Label>
      <div className="grid sm:grid-cols-2 gap-2 mt-1">
        {['onsite', 'mobile', 'remote', 'hybrid'].map((v) => (
          <button key={v} type="button" onClick={() => up({ work_arrangement: v })}
                  className={`text-left p-3 border-2 rounded-lg text-sm ${f.work_arrangement === v ? 'border-brand bg-orange-50' : 'border-slate-200 bg-white'}`}
                  data-testid={`f-arrangement-${v}`}>
            {t(`jobWizard.opts.arrangement.${v}`)}
          </button>
        ))}
      </div>
    </div>

    <div>
      <Label>{t('jobWizard.step2.travelLabel')}</Label>
      <Select value={f.travel_between_sites} onValueChange={(v) => up({ travel_between_sites: v })}>
        <SelectTrigger data-testid="f-travel"><SelectValue placeholder={t('jobWizard.step2.travelPlaceholder')} /></SelectTrigger>
        <SelectContent>
          {['no', 'same_region', 'different_regions', 'different_countries'].map((v) =>
            <SelectItem key={v} value={v}>{t(`jobWizard.opts.travel.${v}`)}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>

    {/* Confirmation map (only when both country + city entered) */}
    {f.country && f.city && (
      <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid="loc-confirm-map">
        <div className="px-3 py-2 bg-slate-50 border-b text-xs font-semibold text-slate-500 uppercase tracking-wide">
          {t('jobWizard.step2.mapConfirm')}
        </div>
        <iframe
          title="location-confirm"
          className="w-full h-52 border-0"
          src={`https://www.openstreetmap.org/export/embed.html?bbox=&layer=mapnik&marker=&q=${encodeURIComponent(`${f.city}, ${countryLabel(f.country, t)}`)}`}
        />
      </div>
    )}
  </div>
);

// ---------- Step 3: Tingimused ----------

const Step3Terms = ({ f, up, errors, t }) => {
  const toggleList = (key, val) => {
    const arr = f[key] || [];
    up({ [key]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] });
  };
  return (
    <div className="space-y-6">
      <div><h2 className="text-xl font-bold text-slate-900">{t('jobWizard.step3.heading')}</h2></div>

      <div>
        <Label>{t('jobWizard.step3.startLabel')} *</Label>
        <div className="grid sm:grid-cols-3 gap-2 mt-1">
          {['asap', 'date', 'agreed'].map((v) => (
            <button key={v} type="button" onClick={() => up({ start_type: v })}
                    className={`p-3 rounded-lg border-2 text-sm ${f.start_type === v ? 'border-brand bg-orange-50' : 'border-slate-200 bg-white'}`}
                    data-testid={`f-start-${v}`}>
              {t(`jobWizard.opts.start.${v}`)}
            </button>
          ))}
        </div>
        {f.start_type === 'date' && (
          <div className="mt-2">
            <Input type="date" value={f.start_date} onChange={(e) => up({ start_date: e.target.value })} data-testid="f-start-date" />
          </div>
        )}
        {errors.start_type && <p className="text-xs text-red-600 mt-1">{errors.start_type}</p>}
      </div>

      <div>
        <Label>{t('jobWizard.step3.durationLabel')} *</Label>
        <Select value={f.duration} onValueChange={(v) => up({ duration: v })}>
          <SelectTrigger data-testid="f-duration"><SelectValue placeholder={t('jobWizard.step3.durationPlaceholder')} /></SelectTrigger>
          <SelectContent>
            {['under_1m', '1_3m', '3_6m', '6_12m', 'over_12m', 'permanent', 'agreed'].map((v) =>
              <SelectItem key={v} value={v}>{t(`jobWizard.opts.duration.${v}`)}</SelectItem>)}
          </SelectContent>
        </Select>
        {errors.duration && <p className="text-xs text-red-600 mt-1">{errors.duration}</p>}
      </div>

      <div>
        <Label>{t('jobWizard.step3.cooperationLabel')} *</Label>
        <Select value={f.cooperation_type} onValueChange={(v) => up({ cooperation_type: v })}>
          <SelectTrigger data-testid="f-cooperation"><SelectValue placeholder={t('jobWizard.step3.cooperationPlaceholder')} /></SelectTrigger>
          <SelectContent>
            {['employment', 'fixed_term', 'subcontract', 'rental_work', 'project', 'other'].map((v) =>
              <SelectItem key={v} value={v}>{t(`jobWizard.opts.coop.${v}`)}</SelectItem>)}
          </SelectContent>
        </Select>
        {errors.cooperation_type && <p className="text-xs text-red-600 mt-1">{errors.cooperation_type}</p>}
      </div>

      <div>
        <Label>{t('jobWizard.step3.hoursLabel')}</Label>
        <div className="grid sm:grid-cols-3 gap-3 mt-1">
          <Input type="number" placeholder={t('jobWizard.step3.hpd')} value={f.hours_per_day}
                 onChange={(e) => up({ hours_per_day: e.target.value })} data-testid="f-hpd" />
          <Input type="number" placeholder={t('jobWizard.step3.dpw')} value={f.days_per_week}
                 onChange={(e) => up({ days_per_week: e.target.value })} data-testid="f-dpw" />
          <Input type="number" placeholder={t('jobWizard.step3.hpw')} value={f.hours_per_week}
                 onChange={(e) => up({ hours_per_week: e.target.value })} data-testid="f-hpw" />
        </div>
        <div className="flex flex-wrap gap-3 mt-2 text-sm">
          {[
            { k: 'overtime_available', l: t('jobWizard.step3.overtime') },
            { k: 'weekend_work',       l: t('jobWizard.step3.weekend') },
            { k: 'night_work',         l: t('jobWizard.step3.night') },
          ].map((o) => (
            <label key={o.k} className="inline-flex items-center gap-2 border rounded-lg px-3 py-2 bg-white">
              <Checkbox checked={!!f[o.k]} onCheckedChange={(v) => up({ [o.k]: !!v })} data-testid={`f-${o.k}`} />
              {o.l}
            </label>
          ))}
        </div>
      </div>

      <div>
        <Label>{t('jobWizard.step3.rotationLabel')}</Label>
        <div className="grid sm:grid-cols-3 gap-2 mt-1">
          {['none', 'yes', 'agreed'].map((v) => (
            <button key={v} type="button" onClick={() => up({ rotation_type: v })}
                    className={`p-3 rounded-lg border-2 text-sm ${f.rotation_type === v ? 'border-brand bg-orange-50' : 'border-slate-200 bg-white'}`}
                    data-testid={`f-rotation-${v}`}>
              {t(`jobWizard.opts.rotation.${v}`)}
            </button>
          ))}
        </div>
        {f.rotation_type === 'yes' && (
          <div className="mt-2">
            <Select value={f.rotation_pattern} onValueChange={(v) => up({ rotation_pattern: v })}>
              <SelectTrigger data-testid="f-rotation-pattern"><SelectValue placeholder={t('jobWizard.step3.rotationPatternPlaceholder')} /></SelectTrigger>
              <SelectContent>
                {['2-1', '3-1', '4-1', 'other'].map((p) => <SelectItem key={p} value={p}>{p === 'other' ? t('jobWizard.opts.other') : p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div>
        <Label>{t('jobWizard.step3.salaryLabel')}</Label>
        <div className="grid sm:grid-cols-4 gap-2 mt-1">
          {['hourly', 'monthly', 'project', 'agreed'].map((v) => (
            <button key={v} type="button" onClick={() => up({ salary_type: v })}
                    className={`p-2 rounded-lg border-2 text-xs ${f.salary_type === v ? 'border-brand bg-orange-50' : 'border-slate-200 bg-white'}`}
                    data-testid={`f-salary-type-${v}`}>
              {t(`jobWizard.opts.salaryPeriod.${v}`)}
            </button>
          ))}
        </div>
        {f.salary_type !== 'agreed' && (
          <div className="grid sm:grid-cols-4 gap-2 mt-2">
            <Input type="number" placeholder="min" value={f.salary_min} onChange={(e) => up({ salary_min: e.target.value })} data-testid="f-salary-min" />
            <Input type="number" placeholder="max" value={f.salary_max} onChange={(e) => up({ salary_max: e.target.value })} data-testid="f-salary-max" />
            <Select value={f.salary_currency} onValueChange={(v) => up({ salary_currency: v })}>
              <SelectTrigger data-testid="f-salary-currency"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['EUR', 'NOK', 'SEK', 'DKK', 'GBP'].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={f.salary_gross_net} onValueChange={(v) => up({ salary_gross_net: v })}>
              <SelectTrigger data-testid="f-salary-gn"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="gross">{t('jobWizard.opts.salary.gross')}</SelectItem>
                <SelectItem value="net">{t('jobWizard.opts.salary.net')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {errors.salary && <p className="text-xs text-red-600 mt-1">{errors.salary}</p>}
      </div>

      <div>
        <Label>{t('jobWizard.step3.accommodationLabel')}</Label>
        <Select value={f.accommodation_type} onValueChange={(v) => up({ accommodation_type: v })}>
          <SelectTrigger data-testid="f-accommodation"><SelectValue placeholder={t('jobWizard.step3.selectOption')} /></SelectTrigger>
          <SelectContent>
            {['company_pays', 'company_arranged_worker_pays', 'worker_arranges', 'not_needed', 'agreed'].map((v) =>
              <SelectItem key={v} value={v}>{t(`jobWizard.opts.accommodation.${v}`)}</SelectItem>)}
          </SelectContent>
        </Select>
        {f.accommodation_type === 'company_arranged_worker_pays' && (
          <Input type="number" className="mt-2" placeholder={t('jobWizard.step3.accCostPlaceholder')}
                 value={f.accommodation_cost} onChange={(e) => up({ accommodation_cost: e.target.value })}
                 data-testid="f-acc-cost" />
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>{t('jobWizard.step3.transportCountry')}</Label>
          <Select value={f.transport_to_country} onValueChange={(v) => up({ transport_to_country: v })}>
            <SelectTrigger data-testid="f-transport-country"><SelectValue placeholder={t('jobWizard.step3.selectOption')} /></SelectTrigger>
            <SelectContent>
              {['company_pays', 'company_arranges', 'worker_arranges', 'agreed', 'not_needed'].map((v) =>
                <SelectItem key={v} value={v}>{t(`jobWizard.opts.transportCountry.${v}`)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>{t('jobWizard.step3.transportLocal')}</Label>
          <Select value={f.transport_local} onValueChange={(v) => up({ transport_local: v })}>
            <SelectTrigger data-testid="f-transport-local"><SelectValue placeholder={t('jobWizard.step3.selectOption')} /></SelectTrigger>
            <SelectContent>
              {['company_car', 'company_arranged', 'public_transport', 'own_car', 'agreed', 'not_needed'].map((v) =>
                <SelectItem key={v} value={v}>{t(`jobWizard.opts.transportLocal.${v}`)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label>{t('jobWizard.step3.equipmentLabel')}</Label>
        <div className="grid sm:grid-cols-2 gap-2 mt-1">
          {['workwear', 'ppe', 'tools', 'work_car'].map((v) => (
            <label key={v} className="inline-flex items-center gap-2 text-sm p-3 border rounded-lg bg-white">
              <Checkbox
                checked={(f.equipment_provided || []).includes(v)}
                onCheckedChange={() => toggleList('equipment_provided', v)}
                data-testid={`f-equipment-${v}`}
              />
              {t(`jobWizard.opts.equipment.${v}`)}
            </label>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 gap-2 mt-2">
          {['own_tools', 'own_car'].map((v) => (
            <label key={v} className="inline-flex items-center gap-2 text-sm p-3 border rounded-lg bg-white">
              <Checkbox
                checked={(f.equipment_worker_own || []).includes(v)}
                onCheckedChange={() => toggleList('equipment_worker_own', v)}
                data-testid={`f-equipment-own-${v}`}
              />
              {t(`jobWizard.opts.equipmentOwn.${v}`)}
            </label>
          ))}
        </div>
      </div>

      <div>
        <Label>{t('jobWizard.step3.benefitsLabel')}</Label>
        <div className="flex flex-wrap gap-2 mt-1">
          {['accommodation_incl', 'transport_incl', 'regular_rotation', 'overtime_possible', 'long_project', 'training', 'work_car', 'tools', 'stable_workload'].map((b) => {
            const sel = (f.benefits || []).includes(b);
            return (
              <button key={b} type="button" onClick={() => toggleList('benefits', b)}
                      className={`px-3 py-1.5 rounded-full text-xs border ${sel ? 'bg-brand text-white border-brand' : 'bg-white border-slate-200'}`}
                      data-testid={`f-benefit-${b}`}>
                {t(`jobWizard.opts.benefits.${b}`)}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ---------- Step 4: Kontrolli ja avalda ----------

const Step4Review = ({ f, up, errors, t, onEdit }) => {
  const toggleQuestion = (q) => {
    const arr = f.screening_questions || [];
    const has = arr.find((x) => x.question === q);
    if (has) up({ screening_questions: arr.filter((x) => x.question !== q) });
    else if (arr.length < 5) up({ screening_questions: [...arr, { question: q, required: true }] });
  };
  const [customQ, setCustomQ] = useState('');
  const addCustom = () => {
    if (!customQ.trim()) return;
    const arr = f.screening_questions || [];
    if (arr.length >= 5) return;
    up({ screening_questions: [...arr, { question: customQ.trim(), required: true }] });
    setCustomQ('');
  };

  const Row = ({ label, val, editStep, testKey }) => (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-slate-100 last:border-0">
      <div className="min-w-0">
        <div className="text-xs text-slate-500">{label}</div>
        <div className="text-sm text-slate-900 truncate">{val || <span className="text-slate-400">—</span>}</div>
      </div>
      <button type="button" onClick={() => onEdit(editStep)} className="text-xs text-brand hover:underline inline-flex items-center gap-1" data-testid={`review-edit-${testKey || editStep}`}>
        <Edit3 className="h-3 w-3" /> {t('jobWizard.edit')}
      </button>
    </div>
  );

  return (
    <div className="space-y-6">
      <div><h2 className="text-xl font-bold text-slate-900">{t('jobWizard.step4.heading')}</h2></div>

      <div className="rounded-xl border border-slate-200 p-4 bg-slate-50/40">
        <Row testKey="title"        label={t('jobWizard.review.title')}         val={f.title} editStep={1} />
        <Row testKey="category"     label={t('jobWizard.review.category')}      val={CATEGORIES.find((c) => c.id === f.category)?.label} editStep={1} />
        <Row testKey="who"          label={t('jobWizard.review.who')}           val={workforceDisplay(f, t, { publicView: false }) || (f.who_type && t(`jobWizard.opts.who.${f.who_type}`))} editStep={1} />
        <Row testKey="location"     label={t('jobWizard.review.location')}      val={[f.city, countryLabel(f.country, t)].filter(Boolean).join(', ')} editStep={2} />
        <Row testKey="duration"     label={t('jobWizard.review.duration')}      val={f.duration && t(`jobWizard.opts.duration.${f.duration}`)} editStep={3} />
        <Row testKey="salary"       label={t('jobWizard.review.salary')}        val={f.salary_type === 'agreed' ? t('jobWizard.preview.salaryAgreed') : `${f.salary_min || ''}${f.salary_max ? `–${f.salary_max}` : ''} ${f.salary_currency}`} editStep={3} />
        <Row testKey="accommodation" label={t('jobWizard.review.accommodation')} val={f.accommodation_type && t(`jobWizard.opts.accommodation.${f.accommodation_type}`)} editStep={3} />
      </div>

      <div>
        <Label>{t('jobWizard.step4.applicationMethod')} *</Label>
        <div className="grid sm:grid-cols-3 gap-2 mt-1">
          {['keikka_only', 'keikka_email', 'keikka_phone'].map((v) => (
            <button key={v} type="button" onClick={() => up({ application_method: v })}
                    className={`p-3 rounded-lg border-2 text-sm ${f.application_method === v ? 'border-brand bg-orange-50' : 'border-slate-200 bg-white'}`}
                    data-testid={`f-appmethod-${v}`}>
              {t(`jobWizard.opts.applicationMethod.${v}`)}
            </button>
          ))}
        </div>
        {errors.application_method && <p className="text-xs text-red-600 mt-1">{errors.application_method}</p>}
        <p className="text-xs text-slate-500 mt-2">{t('jobWizard.step4.applicationMethodHelp')}</p>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <Label>{t('jobWizard.step4.contactPerson')} *</Label>
          <Input value={f.contact_person} onChange={(e) => up({ contact_person: e.target.value })} data-testid="f-contact-person" />
          {errors.contact_person && <p className="text-xs text-red-600 mt-1">{errors.contact_person}</p>}
        </div>
        <div>
          <Label>{t('jobWizard.step4.contactEmail')}</Label>
          <Input type="email" value={f.contact_email} onChange={(e) => up({ contact_email: e.target.value })} data-testid="f-contact-email" />
        </div>
        <div>
          <Label>{t('jobWizard.step4.contactPhone')}</Label>
          <Input value={f.contact_phone} onChange={(e) => up({ contact_phone: e.target.value })} data-testid="f-contact-phone" />
        </div>
      </div>

      <div>
        <Label>{t('jobWizard.step4.screening')}</Label>
        <p className="text-xs text-slate-500 mb-2">{t('jobWizard.step4.screeningHelp')}</p>
        <div className="space-y-1">
          {DEFAULT_SCREENING.map((q, i) => {
            const sel = (f.screening_questions || []).find((x) => x.question === q);
            return (
              <label key={i} className="flex items-start gap-2 text-sm text-slate-700 p-2 border rounded-lg bg-white">
                <Checkbox checked={!!sel} onCheckedChange={() => toggleQuestion(q)}
                          data-testid={`f-screening-${i}`} />
                <span>{q}</span>
              </label>
            );
          })}
        </div>
        {/* Custom question */}
        <div className="flex gap-2 mt-2">
          <Input value={customQ} onChange={(e) => setCustomQ(e.target.value)}
                 placeholder={t('jobWizard.step4.customQPlaceholder')} data-testid="f-screening-custom" />
          <Button variant="outline" onClick={addCustom} disabled={!customQ.trim() || (f.screening_questions || []).length >= 5}
                  data-testid="f-screening-add">
            {t('jobWizard.step4.addQuestion')}
          </Button>
        </div>
      </div>
    </div>
  );
};
